import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasTenantWidePermission } from '@/stores/auth';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';

/**
 * `/masters/working-time-contract-classes` (v0.4後半 月次実績一括入力 第2フェーズ I-1b、§3-3、D-997/D-998)。
 * `working_time_contract_classes` (`apps/api/src/routes/employee-org-masters.ts`) の管理画面。
 * annual_scheduled_holidays は保持のみ (現時点でどの計算エンジンからも参照しない、§3-3)。
 */

type ContractClass = {
  contract_class_id: string; code: string; label: string;
  daily_scheduled_minutes: number | null; monthly_scheduled_minutes: number | null;
  monthly_scheduled_work_days: number | null; annual_scheduled_holidays: number | null;
  sort_order: number;
};

const inputCls = 'w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring';
const numberInputCls = `${inputCls} appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none`;

type Draft = {
  contract_class_id: string | null; code: string; label: string;
  daily_scheduled_minutes: string; monthly_scheduled_minutes: string;
  monthly_scheduled_work_days: string; annual_scheduled_holidays: string;
  sort_order: string;
};

const toNullableNumber = (v: string): number | null => (v.trim() === '' ? null : Number(v));

export function WorkingTimeContractClassesPage() {
  const user = useAuthStore((s) => s.user);
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'skill', 'manage');
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ContractClass | null>(null);

  const { data, isLoading } = useQuery<ContractClass[]>({
    queryKey: ['working-time-contract-classes'],
    queryFn: () => apiFetch<ContractClass[]>('/masters/working-time-contract-classes'),
  });
  const classes = [...(data ?? [])].sort((a, b) => a.sort_order - b.sort_order);

  const saveMut = useMutation({
    mutationFn: (d: Draft) => {
      const payload = {
        code: d.code.trim(), label: d.label.trim(),
        daily_scheduled_minutes: toNullableNumber(d.daily_scheduled_minutes),
        monthly_scheduled_minutes: toNullableNumber(d.monthly_scheduled_minutes),
        monthly_scheduled_work_days: toNullableNumber(d.monthly_scheduled_work_days),
        annual_scheduled_holidays: toNullableNumber(d.annual_scheduled_holidays),
        sort_order: Number(d.sort_order) || 0,
      };
      return d.contract_class_id
        ? apiFetch(`/masters/working-time-contract-classes/${d.contract_class_id}`, {
          method: 'PUT',
          body: JSON.stringify({
            label: payload.label, sort_order: payload.sort_order,
            daily_scheduled_minutes: payload.daily_scheduled_minutes, monthly_scheduled_minutes: payload.monthly_scheduled_minutes,
            monthly_scheduled_work_days: payload.monthly_scheduled_work_days, annual_scheduled_holidays: payload.annual_scheduled_holidays,
          }),
        })
        : apiFetch('/masters/working-time-contract-classes', { method: 'POST', body: JSON.stringify(payload) });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['working-time-contract-classes'] }); setDraft(null); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/masters/working-time-contract-classes/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['working-time-contract-classes'] }); setDeleteTarget(null); toast.success('削除しました'); },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const canSave = !!draft && draft.code.trim() !== '' && draft.label.trim() !== '';

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h1">契約区分マスタ</h1>
        <p className="text-sm text-text-sub mt-1">
          社員に設定する契約区分 (所定労働時間・日数の標準値) を管理します。個別の雇用契約がある社員は、社員詳細画面から個人ごとに上書きできます。
        </p>
        {!canManage && <p className="text-xs text-text-sub mt-1">閲覧のみ可能です（登録・編集には全店舗の管理権限が必要です）。</p>}
      </div>

      {canManage && (
        <div className="flex justify-end">
          <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />}
            onClick={() => setDraft({
              contract_class_id: null, code: '', label: '',
              daily_scheduled_minutes: '', monthly_scheduled_minutes: '', monthly_scheduled_work_days: '', annual_scheduled_holidays: '',
              sort_order: String(classes.length),
            })}
          >契約区分を追加</Button>
        </div>
      )}

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : classes.length === 0 ? (
        <EmptyState message="契約区分がまだありません" description={canManage ? '「契約区分を追加」から登録してください。' : undefined} />
      ) : (
        <ul className="space-y-2">
          {classes.map((c) => (
            <li key={c.contract_class_id} className="flex items-center justify-between gap-2 border border-surface-border rounded-lg bg-white p-3 flex-wrap">
              <div className="min-w-0 flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-text-body break-words">{c.label}</span>
                <span className="text-[11px] text-text-sub whitespace-nowrap">({c.code})</span>
                {c.daily_scheduled_minutes != null && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">1日 {c.daily_scheduled_minutes}分</span>
                )}
                {c.monthly_scheduled_minutes != null && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">月 {c.monthly_scheduled_minutes}分</span>
                )}
                {c.monthly_scheduled_work_days != null && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">月 {c.monthly_scheduled_work_days}日</span>
                )}
              </div>
              {canManage && (
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" aria-label="編集"
                    onClick={() => setDraft({
                      contract_class_id: c.contract_class_id, code: c.code, label: c.label,
                      daily_scheduled_minutes: c.daily_scheduled_minutes != null ? String(c.daily_scheduled_minutes) : '',
                      monthly_scheduled_minutes: c.monthly_scheduled_minutes != null ? String(c.monthly_scheduled_minutes) : '',
                      monthly_scheduled_work_days: c.monthly_scheduled_work_days != null ? String(c.monthly_scheduled_work_days) : '',
                      annual_scheduled_holidays: c.annual_scheduled_holidays != null ? String(c.annual_scheduled_holidays) : '',
                      sort_order: String(c.sort_order),
                    })}
                    className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-text-body focus-ring"><Pencil size={14} /></button>
                  <button type="button" aria-label="削除" onClick={() => setDeleteTarget(c)}
                    className="w-11 h-11 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring"><Trash2 size={14} /></button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!draft} title={draft?.contract_class_id ? '契約区分を編集' : '契約区分を追加'} onClose={() => setDraft(null)} size="sm"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSave || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate(draft)}>保存</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">コード</label>
              <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} maxLength={50}
                className={inputCls} placeholder="例: full_time_standard" disabled={!!draft.contract_class_id} />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">名称</label>
              <input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} maxLength={100}
                className={inputCls} placeholder="例: 正社員標準" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-text-sub mb-1">1日の所定労働時間 (分、任意)</label>
                <input type="number" min={1} value={draft.daily_scheduled_minutes}
                  onChange={(e) => setDraft({ ...draft, daily_scheduled_minutes: e.target.value })} className={numberInputCls} />
              </div>
              <div>
                <label className="block text-sm text-text-sub mb-1">月の所定労働時間 (分、任意)</label>
                <input type="number" min={1} value={draft.monthly_scheduled_minutes}
                  onChange={(e) => setDraft({ ...draft, monthly_scheduled_minutes: e.target.value })} className={numberInputCls} />
              </div>
              <div>
                <label className="block text-sm text-text-sub mb-1">月の所定労働日数 (任意)</label>
                <input type="number" min={1} value={draft.monthly_scheduled_work_days}
                  onChange={(e) => setDraft({ ...draft, monthly_scheduled_work_days: e.target.value })} className={numberInputCls} />
              </div>
              <div>
                <label className="block text-sm text-text-sub mb-1">年間所定休日数 (任意)</label>
                <input type="number" min={0} value={draft.annual_scheduled_holidays}
                  onChange={(e) => setDraft({ ...draft, annual_scheduled_holidays: e.target.value })} className={numberInputCls} />
              </div>
            </div>
            <p className="text-xs text-text-sub">年間所定休日数は現時点では記録のみで、集計には使用されません。</p>
            <div>
              <label className="block text-sm text-text-sub mb-1">並び順</label>
              <input type="number" min={0} value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} className={numberInputCls} />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="契約区分を削除" message={`「${deleteTarget?.label}」を削除します。この操作は取り消せません。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.contract_class_id)} />
    </div>
  );
}
